// ============================================================
// OrivonAI CONTENT FILE
// Edit all site copy, communities, doctors, and features here.
// ============================================================

// ── Site-wide copy ───────────────────────────────────────────
export const COPY = {
  siteName: 'OrivonAI',
  tagline: 'AI-GUIDED CARE FOR EVERY CHAPTER',
  heroHeadline: 'Your Companion for Every Chapter of Womanhood',
  heroHeadlineItalic: '',
  heroSubtitleLead:
    "An intelligent health platform that remembers your history, your stage, and what matters to you — and adapts. Everything gets more useful the longer you're here, with real community alongside you at every step.",
  heroSubtitleRest: '',
  heroCta: 'Join the waitlist',
  heroCtaSecondary: 'See how it works',
  footerTagline: 'A companion for every chapter of your journey',
};

// ── About page: founder story & mission (/about) ─────────────
export const ABOUT_ORIVON = {
  label: 'About us',
  headline:
    'Seven years. Six losses. Two children—and every reason to give others hope.',
  founderLine: 'Vanessa Mosbacker · Founder',
  founderPhoto: '/images/vanessa-mosbacker.png',
  /** Pull quote under photo — losses + hope + why we built the platform */
  founderQuote:
    'If you’ve counted losses, you’re not alone here. I carried hope through every step of my journey—and I built OrivonAI so that same hope has a place to land for you, too.',
  story: [
    'Vanessa’s path through fertility lasted seven years. Along the way she survived six pregnancy losses—each with its own grief, its own quiet that friends and apps rarely know how to hold. She also lived through IVF, hope, disappointment, and eventually surrogacy: a chapter that was beautiful, exhausting, and nowhere to be found in mainstream “mom” content. When her two children finally arrived via surrogate, she stepped into motherhood without a playbook for how they’d come home—or for what the early years would ask of her.',
    'Through it all, she remained hopeful—and she wanted to build a company that could give every woman the hope she had carried through her journey.',
    'OrivonAI exists because that loneliness is too common. It’s for navigating contracts and clinics while your heart is still catching up. It’s for the whiplash of becoming a mom after years of trying—and for anyone who needs a companion that doesn’t look away from loss, surrogacy, or the messy in-between.',
  ],
  bridge: 'That’s why we built OrivonAI: the companion she wished she’d had at 2 a.m.—one that honors six losses and two miracles alike, remembers your story, and stays with you into the fourth trimester and beyond.',
  missionTitle: 'Our mission',
  mission: [
    'Meet every woman exactly where she is—not where a generic checklist says she should be.',
    'Hold space for the hard roads: IVF, surrogacy, pregnancy loss, and postpartum—without looking away.',
    'Pair empathetic AI with stage-matched community and provider guidance so you never have to rebuild your context from scratch.',
    'Build tools that are clinically grounded, privacy-respecting, and always centered on your agency and dignity.',
  ],
};

// CHAPTERS is derived from JOURNEY_OPTIONS below (journey buckets site-wide).

// ── Sign-up: interests within each chapter ───────────────────
export const CHAPTER_INTERESTS = {
  fertility: [
    'IVF or egg freezing',
    'Medication & injection support',
    'Understanding protocols & timelines',
    'Emotional support through treatment',
    'Second opinions or finding an REI',
  ],
  ttc: [
    'Ovulation & fertile windows',
    'Period & cycle tracking',
    'LH tests & timing',
    'How long to try before seeing a doctor',
    'PCOS, endo, or hormones',
    'Loss or trying after loss',
    'Partner communication',
  ],
  pregnancy: [
    "Week-by-week symptoms & what's normal",
    'Appointments, labs, & ultrasounds',
    'Birth prep & preferences',
    'Nutrition, movement, & sleep',
    'Anxiety or high-risk pregnancy',
  ],
  postpartum: [
    'Motherhood: first year to teen years',
    'Mood, PPD, or feeling overwhelmed',
    'Physical recovery & pelvic health',
    'Sleep in the fourth trimester',
    'Returning to work or identity shifts',
    'Partner & relationship changes',
  ],
  baby: [
    'Infant feeding, sleep, and routines',
    'Toddler behavior and early milestones',
    'School-age growth and mental health',
    'Tween/teen communication and boundaries',
    'Activities, camps, and social development',
  ],
};

// Sign-up step 3: detail interests per onboarding journey (JOURNEY_OPTIONS id)
export const SIGNUP_JOURNEY_INTERESTS = {
  ivf: CHAPTER_INTERESTS.fertility,
  ttc: CHAPTER_INTERESTS.ttc,
  surrogacy: [
    'Intended parent journey',
    'Gestational carrier / surrogate',
    'Agency, legal, & logistics',
    'Embryo transfer & pregnancy support',
    'Emotional support for everyone involved',
  ],
  pregnancy: CHAPTER_INTERESTS.pregnancy,
  postpartum: CHAPTER_INTERESTS.postpartum,
  baby: CHAPTER_INTERESTS.baby,
  loss: [
    'Early loss or miscarriage',
    'Later loss or stillbirth',
    'Recurrent pregnancy loss',
    'Trying again after loss',
    'Partner & family support',
    'Peer support & community',
  ],
};

// ── Features section ─────────────────────────────────────────
export const FEATURES = [
  {
    icon: '💬',
    bg: '#FBEAF0',
    title: 'Nova — 24/7 AI companion',
    desc: 'Ask anything at 2am or 2pm. Nova knows your history, understands your chapter, and never makes you repeat yourself.',
  },
  {
    icon: '👥',
    bg: '#E1F5EE',
    title: 'Smart community matching',
    desc: 'The right room, not every room. Nova puts you exactly where you belong — with women who are in your moment, not just your general situation.',
  },
  {
    icon: '🗺️',
    bg: '#EEEDFE',
    title: 'Journey tracker & milestone alerts',
    desc: 'Your cycle mapped from first appointment to last result. Log medications, set injection reminders, and see every milestone at a glance. Nova checks in before you even think to ask.',
  },
  {
    icon: '🏥',
    bg: '#E6F1FB',
    title: 'Doctor & specialist finder',
    desc: 'REI, OB, midwife, lactation consultant — AI-matched to your chapter, location, and insurance.',
  },
  {
    icon: '💗',
    bg: '#FFF1DD',
    title: 'The harder roads, held with care',
    desc: "Surrogacy, pregnancy loss, recurrent failure — Nova doesn't flinch from the painful parts. She meets you there with the right information, the right community, and the kind of support that knows when to just sit with you.",
  },
  {
    icon: '🌡️',
    bg: '#F0ECFF',
    title: 'Ovulation & cycle tracking',
    desc: "Track fertile windows when you're TTC. One place for every stage of your journey.",
  },
];

// ── Teen / First Chapter: mood & support (vetted, non-diagnostic) ─
export const TEEN_SUPPORT_LINKS = [
  { name: 'Crisis Text Line', desc: 'Free 24/7 support. Text HOME to 741741.', url: 'https://www.crisistextline.org/' },
  { name: '988 Suicide & Crisis Lifeline', desc: 'Call or text 988 anytime.', url: 'https://988lifeline.org/' },
  { name: 'Teen Line', desc: 'Teens helping teens. Call, text, or message.', url: 'https://teenlineonline.org/' },
];

// ── Communities ──────────────────────────────────────────────
export const COMMUNITIES = [
  {
    id: 'ivf-warriors',
    icon: '🌸',
    bg: '#FBEAF0',
    name: 'IVF Warriors — Cycle 1',
    desc: 'Support for first-time IVF journeys · Daily check-ins',
    members: 3240,
    tag: 'Fertility',
    tagColor: 'pink',
    lastActivity: 'Sarah: "Retrieval done! 12 eggs retrieved 🎉"',
    unread: 4,
    chapters: ['ivf'],
  },
  {
    id: 'due-summer',
    icon: '🤰',
    bg: '#E1F5EE',
    name: 'Due in Summer 2026',
    desc: 'For women pregnant after IVF · Week-by-week support',
    members: 1180,
    tag: 'Pregnancy',
    tagColor: 'sage',
    lastActivity: 'Week 20 check-in thread is live!',
    unread: 2,
    chapters: ['pregnancy'],
  },
  {
    id: 'fourth-trimester',
    icon: '👶',
    bg: '#E6F1FB',
    name: 'Fourth Trimester Circle',
    desc: 'Postpartum recovery, breastfeeding, PPD support',
    members: 2400,
    tag: 'Postpartum',
    tagColor: 'blue',
    lastActivity: 'New thread: getting through week 6',
    unread: 0,
    chapters: ['postpartum'],
  },
  {
    id: 'first-year-baby',
    icon: '👶',
    bg: '#FFF5F0',
    name: 'First-Year Baby Club',
    desc: 'Sleep, feeding, milestones — without the judgment',
    members: 3100,
    tag: 'Baby',
    tagColor: 'pink',
    lastActivity: 'Thread: 4-month sleep regression tips',
    unread: 1,
    chapters: ['baby'],
  },
  {
    id: 'pcos-sisters',
    icon: '🌙',
    bg: '#EEEDFE',
    name: 'PCOS Sisters',
    desc: 'Cycle regulation, insulin resistance, fertility impact',
    members: 4100,
    tag: 'Trying to Conceive',
    tagColor: 'purple',
    lastActivity: 'New guide: managing PCOS naturally',
    unread: 1,
    chapters: ['ttc', 'ivf'],
  },
  {
    id: 'low-amh',
    icon: '🤍',
    bg: '#E1F5EE',
    name: 'Low AMH Warriors',
    desc: 'Navigating diminished ovarian reserve with hope',
    members: 2100,
    tag: 'Fertility',
    tagColor: 'pink',
    lastActivity: 'Monthly success story thread is open 🌸',
    unread: 0,
    chapters: ['ivf'],
  },
  {
    id: 'mental-health',
    icon: '🌙',
    bg: '#EEEDFE',
    name: 'Fertility & Mental Health',
    desc: 'Because the emotional toll is real. Therapist-moderated.',
    members: 1890,
    tag: 'All chapters',
    tagColor: 'purple',
    lastActivity: 'Managing anxiety before retrieval — new thread',
    unread: 0,
    chapters: ['ivf', 'pregnancy', 'postpartum', 'loss'],
  },
];

// ── Live Chat Rooms (smaller, topic-based, real-time) ─────────
// Designed for immediate answers vs. large forums. Max ~12–20 per room.
export const LIVE_CHAT_ROOMS = [
  { id: 'live-stims', topic: 'Stims support', desc: 'Day 1–12 questions · Gonal-F, Menopur, timing', icon: '💉', chapters: ['ivf'], online: 7, maxSize: 12 },
  { id: 'live-trigger', topic: 'Trigger shot night', desc: 'Tonight’s the night — prep, timing, what to expect', icon: '🎯', chapters: ['ivf'], online: 4, maxSize: 10 },
  { id: 'live-retrieval', topic: 'Retrieval recovery', desc: 'Post-ER tips, what to eat, when to call clinic', icon: '🥚', chapters: ['ivf'], online: 6, maxSize: 12 },
  { id: 'live-transfer', topic: 'Transfer & TWW', desc: 'Embryo transfer prep · Two-week wait support', icon: '🤍', chapters: ['ivf'], online: 9, maxSize: 15 },
  { id: 'live-beta', topic: 'Beta day', desc: 'HCG results, next steps, support either way', icon: '📊', chapters: ['ivf'], online: 3, maxSize: 10 },
  { id: 'live-first-response', topic: 'First response tonight', desc: 'Trigger + 36h — immediate support', icon: '⏰', chapters: ['ivf'], online: 2, maxSize: 8 },
  { id: 'live-ttc', topic: 'TTC this cycle', desc: 'Ovulation, timing, OPKs — quick answers', icon: '💜', chapters: ['ttc'], online: 5, maxSize: 12 },
  { id: 'live-tww', topic: 'Two-week wait', desc: 'Symptom spotting? Need to vent? You’re not alone', icon: '🌸', chapters: ['ttc', 'ivf'], online: 8, maxSize: 15 },
  { id: 'live-pregnancy', topic: 'Early pregnancy', desc: 'First trimester questions · Symptoms, scans', icon: '🤰', chapters: ['pregnancy'], online: 6, maxSize: 12 },
  { id: 'live-postpartum', topic: 'Fourth trimester', desc: 'Sleep, feeding, recovery — right now', icon: '👶', chapters: ['postpartum'], online: 4, maxSize: 12 },
  { id: 'live-ttc-tracking', topic: 'Cycle tracking & TTC', desc: 'Apps, irregular periods, fertile windows — what counts as normal', icon: '💜', chapters: ['ttc'], online: 9, maxSize: 15 },
];

// ── Events (Ohio — fertility, TTC, pregnancy, women's health) ─
export const EVENTS = [
  { id: 'e1', title: 'IVF Support Circle', location: 'Columbus, OH', date: '2025-03-25', time: '6:30 PM', type: 'in-person', chapter: 'ivf', desc: 'Monthly meetup for women going through IVF. Share stories, ask questions, find support.' },
  { id: 'e2', title: 'TTC & Fertility Wellness Workshop', location: 'Cleveland, OH', date: '2025-03-28', time: '10:00 AM', type: 'in-person', chapter: 'ttc', desc: 'Yoga, meditation, and fertility nutrition tips. Led by a certified fertility coach.' },
  { id: 'e3', title: 'Understanding Your Fertility Workup', location: 'Virtual (Ohio)', date: '2025-03-22', time: '12:00 PM', type: 'virtual', chapter: 'ivf', desc: 'Free webinar: AMH, FSH, AFC explained. Q&A with an REI nurse.' },
  { id: 'e4', title: 'Pregnancy After Loss Support Group', location: 'Cincinnati, OH', date: '2025-03-26', time: '7:00 PM', type: 'in-person', chapter: 'pregnancy', desc: 'A safe space for women pregnant after loss. Facilitated by a perinatal therapist.' },
  { id: 'e5', title: 'Egg Freezing 101', location: 'Columbus, OH', date: '2025-04-02', time: '6:00 PM', type: 'in-person', chapter: 'ivf', desc: 'Learn about the process, costs, and what to expect. Panel of women who’ve done it.' },
  { id: 'e6', title: 'Two-Week Wait Virtual Coffee', location: 'Virtual (Ohio)', date: '2025-03-24', time: '9:00 AM', type: 'virtual', chapter: 'ttc', desc: 'Casual Zoom hangout. No agenda — just company during the hardest wait.' },
  { id: 'e7', title: 'Fertility Finance & Grants Info Session', location: 'Virtual (Ohio)', date: '2025-04-05', time: '2:00 PM', type: 'virtual', chapter: 'ivf', desc: 'Grants, employer benefits, and financing options. Real strategies that work.' },
  { id: 'e8', title: 'New Mom Meetup — Fourth Trimester', location: 'Columbus, OH', date: '2025-03-29', time: '11:00 AM', type: 'in-person', chapter: 'postpartum', desc: 'Strollers welcome. Connect with other new moms, share ups and downs.' },
];

// ── Demo profile: Maya (fertility journey) — seeded for empty profiles ─
export const DEMO_PROFILE_MAYA = {
  bio: 'Four years. A few miscarriages. Now on my second round of IVF — and honestly, I’m tired but not giving up. I’m here to find connection with other women who get it. No one should walk this road alone.',
  fertilityJourney: 'IVF · Cycle 2 · Stims · Day 10',
  location: 'Columbus, OH',
  clinic: 'Ohio Fertility Network',
  interests: ['IVF support', 'Low AMH', 'TWW', 'Stims', 'Retrieval prep'],
  birthday: '1992-08-14',
};
export const DEMO_FRIENDS_MAYA = [
  { id: 'df1', identifier: 'w1', name: 'Jasmine Mitchell', createdAt: '2025-03-01T10:00:00' },
  { id: 'df2', identifier: 'w7', name: 'Emma Chen', createdAt: '2025-03-05T14:00:00' },
  { id: 'df3', identifier: 'w5', name: 'Priya Rao', createdAt: '2025-03-08T09:00:00' },
  { id: 'df4', identifier: 'w9', name: 'Lindsay Walsh', createdAt: '2025-03-10T16:00:00' },
];
export const DEMO_POSTS_MAYA = [
  { id: 'dp1', userName: 'Maya', userEmail: '__demo__', topic: 'ivf', groupId: 'ivf-warriors', content: 'Just hit 12 follicles at my last scan — feeling hopeful but nervous. Anyone else at Ohio Fertility Network?', createdAt: '2025-03-12T14:00:00' },
  { id: 'dp2', userName: 'Maya', userEmail: '__demo__', topic: 'ivf', groupId: 'ivf-warriors', content: 'Trigger shot is tonight. Made a checklist and trying to breathe. Sending love to everyone in the same boat. 💕', createdAt: '2025-03-14T09:30:00' },
  { id: 'dp3', userName: 'Maya', userEmail: '__demo__', topic: 'ivf', groupId: null, content: 'Welcome to OrivonAI! So grateful for this community already. 💕', createdAt: '2025-03-19T10:20:32' },
];

// ── Women to Discover (friend discovery — location, clinic, journey, full profile) ─
// area: city for matching user zip. clinic: fertility clinic name.
export const WOMEN_TO_DISCOVER = [
  { id: 'w1', name: 'Jasmine Mitchell', initials: 'JM', location: 'Columbus, OH', area: 'Columbus', clinic: 'Ohio Fertility Network', journey: 'IVF · Cycle 2', bio: 'First IVF cycle. Staying hopeful. Love hiking and good coffee. Would love to connect with others who get it.', interests: ['IVF support', 'Low AMH', 'TWW'], chapters: ['ivf'], birthday: '1992-04-15', roomsJoined: ['ivf-warriors', 'low-amh'], posts: [{ content: 'Just hit 12 follicles at my last scan — feeling hopeful but nervous. Anyone else at Ohio Fertility Network?', createdAt: '2025-03-10T14:00:00' }] },
  { id: 'w2', name: 'Noelle Reed', initials: 'NR', location: 'Cleveland, OH', area: 'Cleveland', clinic: 'Cleveland Clinic Fertility', journey: 'Stims · Day 8', bio: 'Stim day 8 here. Would love to connect with others in the same boat. Cleveland Clinic patient.', interests: ['Stims', 'Retrieval prep'], chapters: ['ivf'], birthday: '1989-08-22', roomsJoined: ['ivf-warriors'], posts: [{ content: 'Trigger shot tonight. Sharing my checklist in case it helps anyone else feeling anxious.', createdAt: '2025-03-12T09:30:00' }] },
  { id: 'w3', name: 'Ava Kim', initials: 'AK', location: 'Cincinnati, OH', area: 'Cincinnati', clinic: 'CCRM Cincinnati', journey: 'Transfer prep', bio: 'Transfer in 2 weeks. Nervous but ready. Mom of one, trying for #2. CCRM patient.', interests: ['Transfer', 'Secondary infertility'], chapters: ['ivf'], birthday: '1987-11-03', roomsJoined: ['ivf-warriors', 'due-summer'], posts: [{ content: 'Transfer scheduled for next week. Any tips from CCRM alumni?', createdAt: '2025-03-11T16:00:00' }] },
  { id: 'w4', name: 'Maya Torres', initials: 'MT', location: 'Columbus, OH', area: 'Columbus', clinic: null, journey: 'TTC · 4 months', bio: 'Natural conception journey. Tracking cycles, learning as I go. Not at a clinic yet.', interests: ['Cycle tracking', 'OPKs', 'Fertile window'], chapters: ['ttc'], birthday: '1994-02-18', roomsJoined: ['pcos-sisters'], posts: [{ content: 'Anyone else balancing sleep recovery and pumping? Looking for what helped you feel human again.', createdAt: '2025-03-09T11:00:00' }] },
  { id: 'w5', name: 'Priya Rao', initials: 'PR', location: 'Columbus, OH', area: 'Columbus', clinic: 'RGI Fertility', journey: 'Beta day tomorrow', bio: 'Second transfer. Whatever happens, I want to support others going through this. RGI patient.', interests: ['TWW', 'Beta results', 'Support'], chapters: ['ivf'], birthday: '1990-06-28', roomsJoined: ['ivf-warriors', 'low-amh'], posts: [{ content: 'Beta tomorrow. Second transfer. Sending love to everyone in the TWW.', createdAt: '2025-03-13T08:00:00' }] },
  { id: 'w6', name: 'Sofia Lopez', initials: 'SL', location: 'Cleveland, OH', area: 'Cleveland', clinic: 'Ohio Fertility Network', journey: 'Postpartum · 6 weeks', bio: 'New mom after IVF. Surviving on coffee and community.', interests: ['Fourth trimester', 'Breastfeeding', 'IVF success'], chapters: ['postpartum'], birthday: '1988-09-14', roomsJoined: ['fourth-trimester', 'ivf-warriors'], posts: [{ content: '6 weeks postpartum after IVF. The journey was worth it. Here for anyone who needs to talk.', createdAt: '2025-03-08T19:00:00' }] },
  { id: 'w7', name: 'Emma Chen', initials: 'EC', location: 'Columbus, OH', area: 'Columbus', clinic: 'Ohio Fertility Network', journey: 'Stims · Day 5', bio: 'First cycle at Ohio Fertility. So far so good. Would love to meet others in Columbus.', interests: ['Stims', 'IVF support'], chapters: ['ivf'], birthday: '1993-01-07', roomsJoined: ['ivf-warriors'], posts: [{ content: 'Day 5 of stims. Feeling okay so far. Gonal-F and Menopur — any tips?', createdAt: '2025-03-14T10:00:00' }] },
  { id: 'w8', name: 'Rachel Brooks', initials: 'RB', location: 'Dayton, OH', area: 'Dayton', clinic: 'Dayton Reproductive Medicine', journey: 'IVF · Cycle 1', bio: 'Just started my first IVF cycle. Nervous and excited. Dayton area.', interests: ['IVF support', 'First cycle'], chapters: ['ivf'], birthday: '1991-05-20', roomsJoined: ['ivf-warriors'], posts: [{ content: 'Consultation done. Starting stims next week. Anyone else at DRM?', createdAt: '2025-03-07T14:00:00' }] },
  { id: 'w9', name: 'Lindsay Walsh', initials: 'LW', location: 'Cincinnati, OH', area: 'Cincinnati', clinic: 'CCRM Cincinnati', journey: 'Retrieval tomorrow', bio: 'Retrieval tomorrow! First cycle. Scared but ready. CCRM family.', interests: ['Retrieval prep', 'IVF support'], chapters: ['ivf'], birthday: '1989-12-11', roomsJoined: ['ivf-warriors'], posts: [{ content: 'Retrieval tomorrow. Any last-minute tips? So nervous!', createdAt: '2025-03-15T07:00:00' }] },
  { id: 'w10', name: 'Dana Foster', initials: 'DF', location: 'Columbus, OH', area: 'Columbus', clinic: 'RGI Fertility', journey: 'TTC · 8 months', bio: 'Trying for 8 months. Starting to look at options. Columbus native.', interests: ['TTC', 'Next steps'], chapters: ['ttc'], birthday: '1992-07-04', roomsJoined: ['pcos-sisters'], posts: [{ content: 'When did you decide to see an RE? 8 months in and wondering.', createdAt: '2025-03-06T12:00:00' }] },
];

// ── Fertility Articles (2025–2026, evidence-based + women's stories) ──────
export const FERTILITY_ARTICLES = [
  { id: 'a1', title: 'Clinical Predictors of IVF Success: What the Data Shows', source: 'NIH / PMC', date: '2025-06', excerpt: 'Systematic review of age, AMH, AFC, embryo quality, and modifiable factors. Evidence-based guidance for women and couples.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=240&fit=crop' },
  { id: 'a2', title: 'Sleep and IVF: What We Learned From 2,000 Women', source: 'BMC Pregnancy & Childbirth', date: '2025-08', excerpt: 'Meta-analysis linking sleep quality to treatment outcomes. Real findings from women in treatment.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1541783245831-57d6fb0926d3?w=400&h=240&fit=crop' },
  { id: 'a3', title: 'ESHRE 2025 Guideline: Ovarian Stimulation for IVF/ICSI', source: 'Reproductive Endocrinology', date: '2025-03', excerpt: '46 new evidence-based recommendations on protocols, monitoring, and fertility preservation.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1581594696397-8f24e3ea63d3?w=400&h=240&fit=crop' },
  { id: 'a4', title: "Three Egg Retrievals: One Woman's Journey Through Fertility Preservation", source: 'The Atlantic', date: '2025-09', excerpt: "A candid account of egg freezing at 33, 35, and 38. The emotional weight, the needles, the hope.", category: 'Stories', evidenceBased: false, img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=240&fit=crop' },
  { id: 'a5', title: 'After Six Losses, I Finally Met My Daughter', source: 'Washington Post', date: '2025-07', excerpt: "One woman's path through recurrent miscarriage, IVF, and surrogacy. Honest, hard, and full of hope.", category: 'Stories', evidenceBased: false, img: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=400&h=240&fit=crop' },
  { id: 'a6', title: 'Fertility in Your 30s: What the Research Says', source: 'Healthline', date: '2025-05', excerpt: 'Evidence-based look at age, decline, and what you can do. Plus real stories from women who conceived.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=240&fit=crop' },
  { id: 'a7', title: 'Coping With the Two-Week Wait', source: 'Resolve', date: '2025-04', excerpt: 'Mental health strategies, community support, and how women get through the hardest wait. Therapist-backed.', category: 'TTC', evidenceBased: true, img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=240&fit=crop' },
  { id: 'a8', title: 'PCOS and Fertility: What Your Doctor Wants You to Know', source: 'Mayo Clinic', date: '2025-06', excerpt: 'Lifestyle, medication, and timing. Practical steps backed by research. Plus stories from women who conceived.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=240&fit=crop' },
  { id: 'a9', title: 'The Cost of Hope: Four Women on Paying for IVF', source: 'New York Times', date: '2025-11', excerpt: 'From grants to second jobs to moving states—how real women made fertility treatment possible.', category: 'Stories', evidenceBased: false, img: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400&h=240&fit=crop' },
  { id: 'a10', title: 'How Common Is Infertility? CDC and NICHD Data', source: 'NICHD / NIH', date: '2025-02', excerpt: '9% of men, 11% of women. Who seeks care, who does not, and what the numbers mean for you.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=240&fit=crop' },
  { id: 'a11', title: 'Unexplained Infertility: When the Tests Come Back Normal', source: 'Cleveland Clinic', date: '2026-01', excerpt: 'Up to half of couples get this diagnosis. What it means, what helps, and stories from women who found answers.', category: 'Evidence-based', evidenceBased: true, img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=240&fit=crop' },
  { id: 'a12', title: 'I Tried IVF Alone—Then With a Partner', source: 'Vogue', date: '2025-10', excerpt: 'One writer on freezing eggs solo, then doing a transfer with her husband. The loneliness, the hope, the support that mattered.', category: 'Stories', evidenceBased: false, img: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=240&fit=crop' },
];

// ── Fertility Products (top-rated) ─────────────────────────────
export const FERTILITY_PRODUCTS = [
  { id: 'p1', name: 'Easy@Home Ovulation Test Strips', brand: 'Easy@Home', rating: 4.8, reviews: 89000, price: 10, excerpt: 'Simple, reliable, affordable. Best value ovulation strips.', category: 'Ovulation', digs: 1247, img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=240&fit=crop' },
  { id: 'p2', name: 'Perelel Conception Support Pack', brand: 'Perelel', rating: 4.9, reviews: 4200, price: 40, excerpt: 'OB/GYN-founded. Iron, CoQ10, selenium in daily packs.', category: 'Supplements', digs: 1023, img: 'https://images.unsplash.com/photo-1550572017-edd951aa2d30?w=400&h=240&fit=crop' },
  { id: 'p3', name: 'ClearBlue Advanced Digital Ovulation', brand: 'Clearblue', rating: 4.7, reviews: 31000, price: 40, excerpt: 'Digital smiley — no line reading. Clear results every time.', category: 'Ovulation', digs: 912, img: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=240&fit=crop' },
  { id: 'p4', name: 'Inito Fertility Tracker', brand: 'Inito', rating: 4.6, reviews: 8500, price: 119, excerpt: 'Smart at-home hormone testing. LH + estrogen in one device.', category: 'Tracking', digs: 756, img: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400&h=240&fit=crop' },
  { id: 'p5', name: 'Proov Predict & Confirm Kit', brand: 'Proov', rating: 4.5, reviews: 6200, price: 27, excerpt: 'Best for PCOS. Predict ovulation and confirm you ovulated.', category: 'Ovulation', digs: 634, img: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=400&h=240&fit=crop' },
  { id: 'p6', name: 'Needed Fertility Support Plan', brand: 'Needed', rating: 4.8, reviews: 2100, price: 49, excerpt: 'Comprehensive fertility-boosting vitamins and minerals.', category: 'Supplements', digs: 521, img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=240&fit=crop' },
  { id: 'p7', name: 'Premom Quantitative Ovulation Strips', brand: 'Premom', rating: 4.6, reviews: 45000, price: 15, excerpt: 'Most detailed results. App scans and tracks your levels.', category: 'Ovulation', digs: 478, img: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=240&fit=crop' },
  { id: 'p8', name: 'Wondfo Ovulation Test Strips', brand: 'Wondfo', rating: 4.7, reviews: 67000, price: 15, excerpt: 'Fastest results. Reliable and widely trusted.', category: 'Ovulation', digs: 445, img: 'https://images.unsplash.com/photo-1581594696397-8f24e3ea63d3?w=400&h=240&fit=crop' },
  { id: 'p9', name: 'Eu Natural Conception Bundle', brand: 'Eu Natural', rating: 4.5, reviews: 3800, price: 56, excerpt: 'For her and him. Hormone balance and cycle support.', category: 'Supplements', digs: 389, img: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=240&fit=crop' },
];

// ── Doctors ──────────────────────────────────────────────────
export const DOCTORS = [
  {
    id: 'jain',
    initials: 'PJ',
    bg: '#FBEAF0',
    color: '#993556',
    avatarBg: '#FBEAF0',
    avatarColor: '#993556',
    name: 'Dr. Priya Jain, MD',
    specialty: 'Reproductive Endocrinology & Infertility',
    clinic: 'RGI (Fertility)',
    location: 'Columbus, OH',
    distance: '1.8 mi',
    rating: 4.9,
    reviews: 412,
    status: 'accepting',
    focus: ['IVF', 'Low AMH', 'Egg retrieval', 'TWW support'],
    chapters: ['ivf'],
    chapter: 'IVF & Fertility',
    bucket: 'ivf',
  },
  {
    id: 'kim',
    initials: 'SM',
    bg: '#E1F5EE',
    color: '#0F6E56',
    avatarBg: '#E1F5EE',
    avatarColor: '#0F6E56',
    name: 'Dr. Sofia Kim, MD',
    specialty: 'REI · Fertility Preservation',
    clinic: 'Ohio Fertility Network',
    location: 'Columbus, OH',
    distance: '4.3 mi',
    rating: 4.7,
    reviews: 268,
    status: 'accepting',
    focus: ['IVF', 'Egg freezing', 'Timing', 'Diminished reserve'],
    chapters: ['ivf'],
    chapter: 'IVF & Fertility',
    bucket: 'ivf',
  },
  {
    id: 'martinez',
    initials: 'AM',
    bg: '#EEEDFE',
    color: '#534AB7',
    avatarBg: '#EEEDFE',
    avatarColor: '#534AB7',
    name: 'Dr. Ana Martinez, MD',
    specialty: 'Maternal-Fetal Medicine & Prenatal Care',
    clinic: 'Wexner Medical OB',
    location: 'Columbus, OH',
    distance: '3.1 mi',
    rating: 4.8,
    reviews: 190,
    status: 'accepting',
    focus: ['High-risk pregnancy', 'Monitoring', 'Answering questions', 'Clear next steps'],
    chapters: ['pregnancy'],
    chapter: 'Pregnancy & Milestones',
    bucket: 'pregnancy',
  },
  {
    id: 'brown',
    initials: 'LB',
    bg: '#FBEAF0',
    color: '#993556',
    avatarBg: '#FBEAF0',
    avatarColor: '#993556',
    name: 'Dr. Lauren Brown, MD',
    specialty: 'OB-GYN · Cycle & Hormones',
    clinic: 'Central Women’s Health',
    location: 'Columbus, OH',
    distance: '2.6 mi',
    rating: 4.6,
    reviews: 155,
    status: 'accepting',
    focus: ['PCOS', 'Endometriosis', 'Cycle regulation', 'Fertility questions'],
    chapters: ['ttc'],
    chapter: 'Trying to Conceive',
    bucket: 'ttc',
  },
  {
    id: 'thompson',
    initials: 'KT',
    bg: '#EAF3DE',
    color: '#3B6D11',
    avatarBg: '#EAF3DE',
    avatarColor: '#3B6D11',
    name: 'Dr. Katie Thompson, MD',
    specialty: 'Postpartum Care & Lactation Medicine',
    clinic: 'Columbus Family Care',
    location: 'Columbus, OH',
    distance: '5.0 mi',
    rating: 4.7,
    reviews: 130,
    status: 'waitlist',
    waitTime: '6 weeks',
    focus: ['Breastfeeding', 'PPD', 'Recovery', 'Sleep support'],
    chapters: ['postpartum'],
    chapter: 'Postpartum & Beyond',
    bucket: 'postpartum',
  },
];

// ── Journey buckets (onboarding, sign-up, landing, journey nav) ─
export const JOURNEY_OPTIONS = [
  { id: 'ivf',        icon: '🌸', bg: '#FBEAF0', color: 'pink',   title: 'IVF — starting or in a cycle',    desc: 'Stimulation, retrieval, transfer support' },
  { id: 'ttc',        icon: '💜', bg: '#EEEDFE', color: 'purple', title: 'Trying to conceive (TTC)',         desc: 'Natural conception, timing, tracking' },
  { id: 'surrogacy',  icon: '🌿', bg: '#EAF3DE', color: 'sage',   title: 'Surrogacy',                        desc: 'As an intended parent or surrogate' },
  { id: 'pregnancy',  icon: '🤰', bg: '#EFF8F5', color: 'coral',  title: 'Pregnancy',                        desc: 'Week-by-week support and guidance' },
  { id: 'postpartum', icon: '🤱', bg: '#E6F1FB', color: 'blue',   title: 'Postpartum',                       desc: 'Fourth trimester, healing, PPD & you' },
  { id: 'baby',       icon: '👶', bg: '#FFF5F0', color: 'pink',   title: 'Motherhood (First year–teen years)', desc: 'From infancy to teen years: milestones, school, and support' },
  { id: 'loss',       icon: '💙', bg: '#E6F1FB', color: 'blue',   title: 'Pregnancy loss',                   desc: 'Finding community and next steps' },
];

/** Alias for filters, journey page, doctors — same ids as JOURNEY_OPTIONS */
export const CHAPTERS = JOURNEY_OPTIONS.map((j) => ({
  id: j.id,
  name: j.title,
  subtitle: j.desc,
  icon: j.icon,
  color: j.color,
}));

/** Landing “Woman Chapters” — one horizontal row. Order: Fertility → TTC → Pregnancy → Motherhood */
export const LANDING_SHOWCASE_CHAPTERS = [
  { id: 'show-fertility', benefitKey: 'ivf', name: 'Fertility', subtitle: '20s → 40s', icon: '🌸', color: 'pink', demoPath: '/demo/fertility/home' },
  { id: 'show-ttc', benefitKey: 'ttc', name: 'Trying to Conceive', subtitle: '20s → 40s', icon: '💜', color: 'purple', demoPath: '/demo/ttc/home' },
  { id: 'show-pregnancy', benefitKey: 'pregnancy', name: 'Pregnancy', subtitle: 'Weeks 1 → Postpartum', icon: '🤰', color: 'coral', demoPath: '/demo/pregnancy/home' },
  { id: 'show-postpartum', benefitKey: 'postpartum', name: 'Motherhood', subtitle: 'Year 1 → Teen Years', icon: '🤱', color: 'blue', demoPath: '/demo/postpartum/home' },
];

/** Hero chapter pills — same titles & icons as Woman Chapters (single source: LANDING_SHOWCASE_CHAPTERS). */
export const HERO_CHAPTER_PILLS = LANDING_SHOWCASE_CHAPTERS.map((c) => ({
  icon: c.icon,
  label: c.name,
}));

/** Landing benefits panel — keyed by journey bucket id */
export const JOURNEY_BENEFITS = {
  ivf: {
    points: [
      'IVF cycle map from consult to beta day.',
      'Saved medication protocols, doses, and reminders.',
      'One place for notes, labs, and big questions.',
      'Step‑by‑step prep so you know what to expect and when to call.',
    ],
    pills: ['IVF timeline', 'Meds & reminders', 'Cycle notes'],
  },
  ttc: {
    points: [
      'Estimate fertile windows from your real cycles.',
      'Log LH tests and what you tried each month.',
      'Arrive with a clear TTC timeline for your doctor.',
      'Reminders for fertile days and follow‑ups.',
    ],
    pills: ['Fertile window', 'LH & timing', 'Ask Nova'],
  },
  surrogacy: {
    points: [
      'Track timelines for intended parents and surrogates.',
      'Plain‑language context on legal, medical, and emotional steps.',
      'Nova remembers where you are in the journey.',
      'Connect with others on a similar path.',
    ],
    pills: ['Journey map', 'Questions for your team', 'Community'],
  },
  pregnancy: {
    points: [
      'Track appointments, labs, and week‑by‑week symptoms.',
      "Quick sense of what's common — and what's worth a call.",
      'Keep birth preferences and visit questions together.',
      'Nova answers in pregnancy‑safe language, 24/7.',
    ],
    pills: ['Appointments', 'Symptoms & peace of mind', 'Birth prep'],
  },
  postpartum: {
    points: [
      'Log mood, sleep, and recovery — focused on you.',
      'Gently highlight patterns that might signal PPD or burnout.',
      'Pelvic health, follow‑ups, and when to reach out.',
      'Space to process the fourth trimester at your pace.',
    ],
    pills: ['Your recovery', 'Mood & sleep', 'Self‑care'],
  },
  baby: {
    points: [
      'Feeding, sleep, and routine tracking for baby’s first year.',
      'Milestones and what to ask at pediatric visits.',
      'First‑year essentials and gear — tailored to you.',
      'Nova stays baby‑focused so you can breathe.',
    ],
    pills: ['Feeding & sleep', 'Milestones', 'First‑year guide'],
  },
  loss: {
    points: [
      'A gentle space for grief, questions, and next steps.',
      'Community with others who understand — no timeline to “move on.”',
      'Plain‑language context for care options and trying again.',
      'Nova meets you where you are, every day.',
    ],
    pills: ['Community', 'Next steps', 'Ask Nova'],
  },
};

// ── IVF journey steps ─────────────────────────────────────────
export const IVF_STEPS = [
  { id: 'consult',       label: 'Consult' },
  { id: 'baseline',      label: 'Baseline' },
  { id: 'stimulation',   label: 'Stims' },
  { id: 'retrieval',     label: 'Retrieval' },
  { id: 'fertilization', label: 'Fertilization' },
  { id: 'transfer',      label: 'Transfer' },
  { id: 'beta',          label: 'Beta HCG' },
];

// ── Medication how-to videos (Nova links out to instructional content) ───
// Match by normalized name (case-insensitive, common variants)
export const MEDICATION_VIDEOS = {
  'gonal-f': {
    url: 'https://www.emdseronofertility.com/en/patient/taking-your-medication/how-to-inject/injection-gonal-f-75-iu.html',
    type: 'external',
    title: 'How to inject Gonal-F (EMD Serono)',
  },
  'gonal f': { url: 'https://www.emdseronofertility.com/en/patient/taking-your-medication/how-to-inject/injection-gonal-f-75-iu.html', type: 'external', title: 'How to inject Gonal-F (EMD Serono)' },
  'gonalf': { url: 'https://www.emdseronofertility.com/en/patient/taking-your-medication/how-to-inject/injection-gonal-f-75-iu.html', type: 'external', title: 'How to inject Gonal-F (EMD Serono)' },
  'menopur': {
    url: 'https://www.youtube.com/embed/k9dPu3zu9h4',
    type: 'youtube',
    title: 'How to inject Menopur',
  },
  'ganirelix': {
    url: 'https://www.organon.com/product/usa/pi_circulars/g/ganirelix/ganirelix_ppi.pdf',
    type: 'external',
    title: 'Ganirelix injection guide (Organon)',
  },
  'cetrotide': {
    url: 'https://www.emdseronofertility.com/en/patient/taking-your-medication/how-to-inject.html',
    type: 'external',
    title: 'Fertility injection guides (EMD Serono)',
  },
  'lupron': {
    url: 'https://www.abbvie.com/our-science/pipeline/franchise/womens-health.html',
    type: 'external',
    title: 'Lupron injection information',
  },
  'pio': { url: 'https://www.youtube.com/results?search_query=progesterone+in+oil+injection+how+to', type: 'external', title: 'Progesterone in oil injection' },
  'progesterone in oil': { url: 'https://www.youtube.com/results?search_query=progesterone+in+oil+injection+how+to', type: 'external', title: 'Progesterone in oil injection' },
};

export function getMedicationVideo(medicationName) {
  if (!medicationName || typeof medicationName !== 'string') return null;
  const key = medicationName.toLowerCase().replace(/[- \s]+/g, ' ').trim();
  const direct = MEDICATION_VIDEOS[key];
  if (direct) return direct;
  for (const [k, v] of Object.entries(MEDICATION_VIDEOS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return null;
}

// ── IVF step prep (what to expect, pack, ask) ──────────────────
export const IVF_STEP_PREP = {
  consult: {
    tagline: 'Your first consult — what to bring and ask.',
    bullets: ['Questions to ask about protocol and timeline', 'What to bring (ID, insurance, prior records)', 'How to prepare for baseline and next steps'],
    novaPrompt: 'I have my first IVF consult coming up. What should I ask and what should I bring?',
  },
  baseline: {
    tagline: 'Baseline visit — you’re officially in the cycle.',
    bullets: ['What happens at baseline (ultrasound, labs)', 'When stims typically start', 'What to track before first injection'],
    novaPrompt: 'I’m at my baseline visit for IVF. What should I expect and what happens next?',
  },
  stimulation: {
    tagline: 'Stims — injection schedule and what to expect.',
    bullets: ['What to pack for injection time', 'Typical side effects and when to call the clinic', 'How many days of stims and when you’ll know retrieval date'],
    novaPrompt: 'I’m on IVF stims. What side effects are normal and when should I call my clinic?',
  },
  retrieval: {
    tagline: 'Retrieval day — prep and recovery.',
    bullets: ['What to bring (ID, comfy clothes, driver)', 'What the procedure feels like and recovery same day', 'When to call about OHSS or pain'],
    novaPrompt: 'My egg retrieval is tomorrow. What should I bring and what should I expect?',
  },
  fertilization: {
    tagline: 'Fertilization report — what the numbers mean.',
    bullets: ['How to read your fertilization report', 'What’s typical and what to ask your clinic', 'When you’ll hear about day 3 vs day 5'],
    novaPrompt: 'I just got my fertilization report. How do I understand the numbers and what should I ask my clinic?',
  },
  transfer: {
    tagline: 'Transfer day — prep and the two-week wait.',
    bullets: ['What to bring and what to expect at transfer', 'Rest, activity, and meds after transfer', 'When to test and how to get through the wait'],
    novaPrompt: 'I have my embryo transfer coming up. What should I expect and how do I get through the two-week wait?',
  },
  beta: {
    tagline: 'Beta day — results and next steps.',
    bullets: ['What beta numbers mean (first and repeat)', 'When to expect first ultrasound', 'If results aren’t what you hoped — what’s next'],
    novaPrompt: 'I’m waiting for my beta results (or just got them). What do the numbers mean and what happens next?',
  },
};

// ── Nova personalized first message ───────────────────────────
export function getNovaFirstMessage(firstName, journeyType, journeyDetail) {
  const name = (firstName || 'there').trim();
  const detail = journeyDetail ? ` I see you're focused on ${journeyDetail}.` : '';
  const msg = {
    ttc: `Hi ${name} 💕 I'm Nova. I've set up your cycle tracker and matched you with a community of women who get it. I'll check in around your fertile window and the two-week wait — you're not alone in this.${detail}`,
    fertility: `Hi ${name} 💕 I'm Nova. I've got your treatment timeline and I'll be here before retrievals, transfers, and beta days. Your community of fertility warriors is ready when you need them.${detail}`,
    ivf: `Hi ${name} 💕 I'm Nova. I've got your treatment timeline and I'll be here before retrievals, transfers, and beta days. Your community of fertility warriors is ready when you need them.${detail}`,
    pregnancy: `Hi ${name} 💕 I'm Nova. I've set up week-by-week guidance and a community at your exact stage. I'll check in as you hit new milestones — and I'm here for the 2am questions too.${detail}`,
    postpartum: `Hi ${name} 💕 I'm Nova. I've set up your postpartum space and matched you with moms who get it. I'll check in as your little one hits new stages — and I'm here when it's hard.${detail}`,
    loss: `Hi ${name} 💕 I'm Nova. I hold space for your loss and your path forward. I'll remember the dates that matter, and your community is here when you're ready. No pressure — I move at your pace.${detail}`,
    surrogacy: `Hi ${name} 💕 I'm Nova. I've set up resources for your surrogacy journey — whether you're an intended parent or a surrogate. Your community gets this path.${detail}`,
  };
  return msg[journeyType] || msg.fertility || `Hi ${name} 💕 I'm Nova, your OrivonAI companion. I'm here for every part of your journey — ask me anything, anytime.${detail}`;
}

// ── Nova chat suggestions by chapter ──────────────────────────
export const NOVA_SUGGESTIONS = {
  default:    ['What chapter should I start with?', 'How does OrivonAI work?', 'What is Nova?', 'How do I find a doctor?'],
  ivf:        ['What should I ask at my monitoring appointment?', 'What does my AMH number mean?', "I'm feeling anxious about my results"],
  fertility:  ['What should I ask at my monitoring appointment?', 'What does my AMH number mean?', "I'm feeling anxious about my results"],
  ttc:        ['When is my fertile window?', 'How do I read an OPK?', 'When should I see a doctor?'],
  pregnancy:  ["What's happening with my baby this week?", 'Is this symptom normal?', 'Help me prepare for my next appointment'],
  postpartum: ["Is this normal for my baby's age?", "I'm struggling with my mood", 'Find activities near me'],
  surrogacy:  ['What should I know as an intended parent?', 'What does a surrogate journey look like?', 'How do I find an agency?', 'How do I prepare emotionally?'],
  baby:       ['Is this sleep pattern normal?', 'When do babies typically start solids?', 'How do I know if feeding is going well?', 'What milestones happen at 6 months?'],
  loss:       ['How do I cope after a loss?', 'When is it okay to try again?', 'How do I support my partner?', 'Where can I find others who understand?'],
};
